import React, { useState } from 'react';
import { 
  Modal, 
  Timeline, 
  Card, 
  Typography, 
  Space, 
  Tag, 
  Button, 
  Select, 
  Row,
  Col, 
  Spin, 
  Empty,
  Descriptions,
  Alert,
  Divider
} from 'antd';
import {
  HistoryOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useResponsive } from '../../hooks/useResponsive';
import { 
  useGetTableEventsQuery, 
  TableEvent, 
  TableEventType,
  TableOpenedEventData,
  TableClosedEventData,
  TableClosedForceEventData,
  TableStatusChangedEventData,
  TableTransferredEventData,
  TableReassignedEventData
} from '../../services/tableEventsApi';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';

const { Title, Text } = Typography;
const { Option } = Select;

// Configure dayjs locale
dayjs.locale('es');

// Helper function to translate table status to Spanish
const translateTableStatus = (status: string): string => {
  const statusTranslations: Record<string, string> = {
    'Available': 'Disponible',
    'Occupied': 'Ocupada',
    'Reserved': 'Reservada',
    'Dirty': 'Sucia',
    'PreCheck': 'Precuenta',
    'Closed': 'Cerrada'
  };
  return statusTranslations[status] || status;
};

interface TableHistoryProps {
  tableId: string;
  tableName: string;
  visible: boolean;
  onClose: () => void;
}

const TableHistory: React.FC<TableHistoryProps> = ({
  tableId,
  tableName,
  visible,
  onClose,
}) => {
  const { isMobile } = useResponsive();
  const [selectedEventType, setSelectedEventType] = useState<TableEventType | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const { 
    data: eventsResponse, 
    isLoading, 
    error,
    refetch 
  } = useGetTableEventsQuery({
    tableId,
    eventType: selectedEventType,
    startDate: dateRange?.[0],
    endDate: dateRange?.[1],
    limit: 100
  }, {
    skip: !visible
  });

  const events = eventsResponse?.events || [];

  // Event type configurations
  const eventTypeConfig: Record<TableEventType, {
    color: string;
    icon: React.ReactNode;
    label: string;
    description: string;
  }> = {
    TableOpened: {
      color: '#52c41a',
      icon: <CheckCircleOutlined />,
      label: 'Mesa Abierta',
      description: 'La mesa fue abierta para clientes'
    },
    TableClosed: {
      color: '#1890ff',
      icon: <CheckCircleOutlined />,
      label: 'Mesa Cerrada',
      description: 'La mesa fue cerrada después del pago'
    },
    TableClosedForce: {
      color: '#ff4d4f',
      icon: <CloseCircleOutlined />,
      label: 'Mesa Cerrada (Forzado)',
      description: 'La mesa fue cerrada forzadamente'
    },
    TableStatusChanged: {
      color: '#fa8c16',
      icon: <SwapOutlined />,
      label: 'Cambio de Estado',
      description: 'El estado de la mesa fue modificado'
    },
    TableTransferred: {
      color: '#722ed1',
      icon: <SwapOutlined />,
      label: 'Mesa Transferida',
      description: 'La mesa fue transferida a otra ubicación'
    },
    TableReassigned: {
      color: '#13c2c2',
      icon: <UserOutlined />,
      label: 'Mesa Reasignada',
      description: 'La mesa fue asignada a otro mesero'
    },
    TableMerged: {
      color: '#eb2f96',
      icon: <SwapOutlined />,
      label: 'Mesa Fusionada',
      description: 'La mesa fue fusionada con otra mesa'
    },
    TableSplit: {
      color: '#52c41a',
      icon: <SwapOutlined />,
      label: 'Mesa Dividida',
      description: 'La mesa fue dividida en múltiples mesas'
    }
  };

  const parseEventData = (event: TableEvent) => {
    try {
      return JSON.parse(event.eventData);
    } catch {
      return {};
    }
  };

  const renderEventDetails = (event: TableEvent) => {
    const eventData = parseEventData(event);

    switch (event.eventType) {
      case 'TableOpened': {
        const data = eventData as TableOpenedEventData;
        return (
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="Comensales">
              {data.customerCount} personas
              {data.adults && data.children && (
                <Text type="secondary" style={{ marginLeft: '8px' }}>
                  ({data.adults} adultos, {data.children} niños)
                </Text>
              )}
            </Descriptions.Item>
            {data.waiterName && (
              <Descriptions.Item label="Mesero">{data.waiterName}</Descriptions.Item>
            )}
            {data.notes && (
              <Descriptions.Item label="Notas">{data.notes}</Descriptions.Item>
            )}
            <Descriptions.Item label="Estado Anterior">{translateTableStatus(data.previousStatus)}</Descriptions.Item>
          </Descriptions>
        );
      }

      case 'TableClosed': {
        const data = eventData as TableClosedEventData;
        return (
          <Descriptions size="small" column={1}>
            {data.orderTotal && (
              <Descriptions.Item label="Total de la Orden">
                ${data.orderTotal.toFixed(2)}
              </Descriptions.Item>
            )}
            {data.paymentMethod && (
              <Descriptions.Item label="Método de Pago">{data.paymentMethod}</Descriptions.Item>
            )}
            {data.reason && (
              <Descriptions.Item label="Motivo">{data.reason}</Descriptions.Item>
            )}
            <Descriptions.Item label="Estado Anterior">{translateTableStatus(data.previousStatus)}</Descriptions.Item>
          </Descriptions>
        );
      }

      case 'TableClosedForce': {
        const data = eventData as TableClosedForceEventData;
        return (
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="Motivo">{data.reason}</Descriptions.Item>
            <Descriptions.Item label="Autorizado por">{data.authorizedBy}</Descriptions.Item>
            {data.orderTotal !== undefined && (
              <Descriptions.Item label="Total de la Orden">
                ${data.orderTotal.toFixed(2)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Estado Anterior">{translateTableStatus(data.previousStatus)}</Descriptions.Item>
          </Descriptions>
        );
      }

      case 'TableStatusChanged': {
        const data = eventData as TableStatusChangedEventData;
        return (
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="Estado Anterior">{translateTableStatus(data.previousStatus)}</Descriptions.Item>
            <Descriptions.Item label="Estado Nuevo">{translateTableStatus(data.newStatus)}</Descriptions.Item>
            {data.reason && (
              <Descriptions.Item label="Motivo">{data.reason}</Descriptions.Item>
            )}
            {data.authorizedBy && (
              <Descriptions.Item label="Autorizado por">{data.authorizedBy}</Descriptions.Item>
            )}
          </Descriptions>
        );
      }

      case 'TableTransferred': {
        const data = eventData as TableTransferredEventData;
        return (
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="Mesa Origen">
              Mesa {data.fromTableNumber} ({data.fromAreaName})
            </Descriptions.Item>
            <Descriptions.Item label="Mesa Destino">
              Mesa {data.toTableNumber} ({data.toAreaName})
            </Descriptions.Item>
            <Descriptions.Item label="Autorizado por">{data.authorizedBy}</Descriptions.Item>
            {data.reason && (
              <Descriptions.Item label="Motivo">{data.reason}</Descriptions.Item>
            )}
          </Descriptions>
        );
      }

      case 'TableReassigned': {
        const data = eventData as TableReassignedEventData;
        return (
          <Descriptions size="small" column={1}>
            {data.previousWaiterName && (
              <Descriptions.Item label="Mesero Anterior">{data.previousWaiterName}</Descriptions.Item>
            )}
            <Descriptions.Item label="Mesero Nuevo">{data.newWaiterName}</Descriptions.Item>
            <Descriptions.Item label="Autorizado por">{data.authorizedBy}</Descriptions.Item>
            {data.reason && (
              <Descriptions.Item label="Motivo">{data.reason}</Descriptions.Item>
            )}
          </Descriptions>
        );
      }

      default:
        return (
          <Text type="secondary">Datos del evento no disponibles</Text>
        );
    }
  };

  const handleClearFilters = () => {
    setSelectedEventType(undefined);
    setDateRange(null);
  };

  const hasFilters = selectedEventType || dateRange;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '100vw' : 900}
      style={{
        // Mismo defecto que traía PaymentModal: un maxWidth en línea le gana al
        // `max-width: calc(100vw - 32px)` de AntD, y con `centered` el modal no
        // cabe junto al puntal de centrado y cae una pantalla más abajo.
        top: isMobile ? 0 : undefined,
        maxWidth: isMobile ? '100vw' : 'calc(100vw - 32px)',
        height: isMobile ? '100vh' : 'auto'
      }}
      styles={{ 
        body: { 
          padding: 0,
          height: isMobile ? '100vh' : 'calc(90vh - 100px)',
          overflowY: 'auto'
        } 
      }}
      destroyOnClose
      centered={!isMobile}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ 
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <Space>
              <HistoryOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
              <Title level={4} style={{ margin: 0 }}>
                Historial de Mesa
              </Title>
            </Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
              size={isMobile ? "small" : "middle"}
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <Space>
              <EnvironmentOutlined />
              <Text strong>{tableName}</Text>
              {events.length > 0 && (
                <Tag color="blue">{events.length} eventos</Tag>
              )}
            </Space>
            
            {hasFilters && (
              <Button 
                size="small" 
                onClick={handleClearFilters}
                style={{ color: '#ff4d4f' }}
              >
                Limpiar Filtros
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ 
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-primary)'
        }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <Space>
                <FilterOutlined />
                <Text strong style={{ fontSize: '12px' }}>Tipo de Evento:</Text>
              </Space>
              <Select
                placeholder="Todos los eventos"
                value={selectedEventType}
                onChange={setSelectedEventType}
                allowClear
                style={{ width: '100%', marginTop: '4px' }}
                size={isMobile ? "small" : "middle"}
              >
                {Object.entries(eventTypeConfig).map(([type, config]) => (
                  <Option key={type} value={type}>
                    <Space>
                      <span style={{ color: config.color }}>{config.icon}</span>
                      {config.label}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12}>
              <Space>
                <ClockCircleOutlined />
                <Text strong style={{ fontSize: '12px' }}>Rango de Fechas:</Text>
              </Space>
              <MobileDateRangePicker
                value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([
                      dates[0].toISOString(),
                      dates[1].toISOString()
                    ]);
                  } else {
                    setDateRange(null);
                  }
                }}
                style={{ width: '100%', marginTop: '4px' }}
                size={isMobile ? "small" : "middle"}
                placeholder={['Fecha inicio', 'Fecha fin']}
              />
            </Col>
          </Row>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          padding: isMobile ? '12px 16px' : '16px 24px',
          overflow: 'auto'
        }}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary">Cargando historial...</Text>
              </div>
            </div>
          )}

          {error && (
            <Alert
              message="Error al cargar el historial"
              description="No se pudo cargar el historial de eventos de la mesa"
              type="error"
              style={{ marginBottom: '16px' }}
            />
          )}

          {!isLoading && !error && events.length === 0 && (
            <Empty
              description="No hay eventos registrados para esta mesa"
              style={{ padding: '40px' }}
            />
          )}

          {!isLoading && !error && events.length > 0 && (
            <Timeline mode={isMobile ? "left" : "alternate"}>
              {events.map((event, index) => {
                const config = eventTypeConfig[event.eventType];
                const eventDate = dayjs(event.createdAt);

                return (
                  <Timeline.Item
                    key={event.id}
                    dot={
                      <div style={{
                        backgroundColor: config.color,
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {config.icon}
                      </div>
                    }
                  >
                    <Card 
                      size="small" 
                      style={{ 
                        marginBottom: '8px',
                        maxWidth: isMobile ? '100%' : '400px'
                      }}
                    >
                      <div style={{ marginBottom: '8px' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            flexWrap: 'wrap'
                          }}>
                            <Tag color={config.color} style={{ margin: 0 }}>
                              {config.label}
                            </Tag>
                            <Text 
                              type="secondary" 
                              style={{ 
                                fontSize: '11px',
                                marginTop: isMobile ? '4px' : '0'
                              }}
                            >
                              {eventDate.format('DD/MM/YYYY HH:mm')}
                            </Text>
                          </div>
                          
                          {event.createdByName && (
                            <div>
                              <UserOutlined style={{ marginRight: '4px', color: 'var(--color-neutral-text)' }} />
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {event.createdByName}
                              </Text>
                            </div>
                          )}
                        </Space>
                      </div>
                      
                      <Divider style={{ margin: '8px 0' }} />
                      
                      {renderEventDetails(event)}
                    </Card>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TableHistory;